import { useMemo, useState } from 'react'
import { History, ChevronLeft, ChevronRight, ShieldX, Trash2, Loader2, Paperclip, X } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useEmailLogs, useEmailSuprimidos, useEliminarSupresion, useEmailLogFacetas } from '@/hooks/useEmailLog'
import { useClienteNombres } from '@/hooks/useClienteNombres'
import type { EmailLogFaceta, EmailLogFilters } from '@/services/emailLogService'
import { etiquetaEstadoEmail, etiquetaTipoEmail, opcionesDeFiltroEmail } from '@/lib/email-labels'
import { EmailDetailSheet } from './EmailDetailSheet'

const PAGE_SIZE = 10

const TODOS = '__all__'

/** Valores de una faceta, listos para `opcionesDeFiltroEmail`. */
const valoresDe = (facetas: EmailLogFaceta[] | undefined) =>
  (facetas ?? []).map((faceta) => faceta.valor)

export function EmailHistorySection({ organizacionId }: { organizacionId?: string }) {
  const [filtros, setFiltros] = useState<EmailLogFilters>({
    organizacionId,
    limit: PAGE_SIZE,
    offset: 0,
  })
  // El listado guarda la organización en su estado y las facetas la leen de la prop. Auditoría
  // monta este componente sin `key`, así que al cambiar de organización no se remonta y las dos
  // mitades quedarían consultando organizaciones distintas.
  const [organizacionAplicada, setOrganizacionAplicada] = useState(organizacionId)
  if (organizacionId !== organizacionAplicada) {
    setOrganizacionAplicada(organizacionId)
    setFiltros((previos) => ({ ...previos, organizacionId, offset: 0 }))
  }
  const [tab, setTab] = useState<'historial' | 'suprimidos'>('historial')
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)

  const { data: logsResult, isLoading: loadingLogs } = useEmailLogs(filtros)
  const { data: facetas, isError: fallaronFacetas } = useEmailLogFacetas(organizacionId)
  const { data: suprimidos, isLoading: loadingSuprimidos } = useEmailSuprimidos(organizacionId)
  const eliminarSupresion = useEliminarSupresion()
  const { clientes, getNombre } = useClienteNombres()

  const logs = logsResult?.data ?? []
  const totalLogs = logsResult?.count ?? 0
  const currentPage = Math.floor((filtros.offset ?? 0) / PAGE_SIZE)
  const totalPages = Math.ceil(totalLogs / PAGE_SIZE)

  const handlePageChange = (page: number) => {
    setFiltros({ ...filtros, offset: page * PAGE_SIZE })
  }

  const opcionesTipo = useMemo(
    () => opcionesDeFiltroEmail(valoresDe(facetas?.tipos), filtros.tipo, etiquetaTipoEmail),
    [facetas?.tipos, filtros.tipo],
  )
  const opcionesEstado = useMemo(
    () =>
      opcionesDeFiltroEmail(valoresDe(facetas?.estados), filtros.status, (valor) =>
        etiquetaEstadoEmail(valor).label,
      ),
    [facetas?.estados, filtros.status],
  )
  const hayFiltros = !!(filtros.tipo || filtros.status || filtros.clienteId)

  const handleFilterChange = (key: keyof EmailLogFilters, value: string) => {
    setFiltros({ ...filtros, [key]: value === TODOS ? undefined : value, offset: 0 })
  }

  const handleLimpiarFiltros = () => {
    setFiltros({ organizacionId, limit: PAGE_SIZE, offset: 0 })
  }

  const handleConfirmDelete = async () => {
    if (confirmDelete) {
      try {
        await eliminarSupresion.mutateAsync(confirmDelete)
        setConfirmDelete(null)
      } catch {
        // Error already handled by mutation onError
      }
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Historial de Emails
        </CardTitle>
        <CardDescription>
          Registro de emails enviados y lista de supresion
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tabs */}
        <div className="flex gap-2">
          <Button
            variant={tab === 'historial' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTab('historial')}
          >
            <History className="h-4 w-4 mr-1" />
            Historial ({totalLogs})
          </Button>
          <Button
            variant={tab === 'suprimidos' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTab('suprimidos')}
          >
            <ShieldX className="h-4 w-4 mr-1" />
            Suprimidos ({suprimidos?.length ?? 0})
          </Button>
        </div>

        {tab === 'historial' && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Tipo de email</Label>
                <Select
                  value={filtros.tipo ?? TODOS}
                  onValueChange={(v) => handleFilterChange('tipo', v)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TODOS}>Todos</SelectItem>
                    {opcionesTipo.map((opcion) => (
                      <SelectItem key={opcion.valor} value={opcion.valor}>{opcion.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Estado del envío</Label>
                <Select
                  value={filtros.status ?? TODOS}
                  onValueChange={(v) => handleFilterChange('status', v)}
                >
                  <SelectTrigger className="w-[170px]">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TODOS}>Todos</SelectItem>
                    {opcionesEstado.map((opcion) => (
                      <SelectItem key={opcion.valor} value={opcion.valor}>{opcion.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Cliente</Label>
                <Select
                  value={filtros.clienteId ?? TODOS}
                  onValueChange={(v) => handleFilterChange('clienteId', v)}
                >
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Todos los clientes" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value={TODOS}>Todos los clientes</SelectItem>
                    {clientes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {hayFiltros && (
                <Button variant="ghost" size="sm" onClick={handleLimpiarFiltros}>
                  <X className="h-4 w-4 mr-1" />
                  Limpiar filtros
                </Button>
              )}
            </div>

            {fallaronFacetas && (
              <p className="text-xs text-destructive">
                No se pudieron cargar las opciones de tipo y estado. Los desplegables quedan vacíos;
                el listado de abajo no está afectado.
              </p>
            )}

            {/* Table */}
            {loadingLogs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : logs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No hay emails registrados
              </p>
            ) : (
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Destinatario</TableHead>
                      <TableHead>Asunto</TableHead>
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => {
                      const statusInfo = etiquetaEstadoEmail(log.status)
                      return (
                        <TableRow
                          key={log.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setSelectedEmailId(log.id)}
                        >
                          <TableCell className="whitespace-nowrap text-sm">
                            {new Date(log.created_at).toLocaleString('es-AR', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </TableCell>
                          <TableCell className="text-sm max-w-[160px] truncate">
                            {getNombre(log.cliente_id) ?? (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {etiquetaTipoEmail(log.tipo)}
                          </TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate">
                            {log.to?.join(', ')}
                          </TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate">
                            <span className="inline-flex items-center gap-1">
                              {log.attachments_meta?.length ? (
                                <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
                              ) : null}
                              {log.subject}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                            {log.error && (
                              <p className="text-xs text-destructive mt-1 max-w-[150px] truncate" title={log.error}>
                                {log.error}
                              </p>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-2">
                <p className="text-sm text-muted-foreground">
                  {totalLogs} emails en total
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 0}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {currentPage + 1} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= totalPages - 1}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {tab === 'suprimidos' && (
          <>
            {loadingSuprimidos ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !suprimidos || suprimidos.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No hay emails suprimidos
              </p>
            ) : (
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Razon</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suprimidos.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="text-sm">{entry.email}</TableCell>
                        <TableCell>
                          <Badge variant={entry.reason === 'manual' ? 'secondary' : 'destructive'}>
                            {entry.reason === 'bounce' ? 'Rebote' :
                             entry.reason === 'complaint' ? 'Queja' : 'Manual'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {new Date(entry.created_at).toLocaleDateString('es-AR')}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmDelete(entry.id)}
                            title="Remover supresion"
                          >
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-3">
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Los emails suprimidos no recibiran mas correos del sistema.
                Si remueves una supresion, el email volvera a recibir correos normalmente.
              </p>
            </div>
          </>
        )}
      </CardContent>

      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover supresion</AlertDialogTitle>
            <AlertDialogDescription>
              Este email volvera a recibir correos del sistema. Si fue suprimido por un rebote,
              es posible que vuelva a rebotar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EmailDetailSheet
        emailId={selectedEmailId}
        organizacionId={organizacionId}
        open={!!selectedEmailId}
        onOpenChange={(open) => { if (!open) setSelectedEmailId(null) }}
      />
    </Card>
  )
}
